// src/components/tasks/TaskService.js
// ✅ DROP-IN (NO “CONFIRMED” COMPLETION): any user who sets status="Completed" completes the task.
// ✅ Keeps: notifications, attachments sanitize, joint perUser logic, sampleOrder reverse-flow update.

import { auth, db } from "../../firebase";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import {
  notifyTaskAssignedToUser,
  notifyTaskAssignedToGroup,
} from "../../utils/notifications";
import { moveDocToRecycleBin } from "../../utils/recycleBinService";

const STAFF_ROLES = new Set(["owner", "admin", "enduser"]);

function roleLower(role) {
  return (role || "").toString().trim().toLowerCase();
}

function isCustomer(me) {
  return roleLower(me?.role) === "customer";
}

function isAdminOrOwnerRole(role) {
  const r = roleLower(role);
  return r === "owner" || r === "admin";
}

function normalizeTask(raw) {
  const dueAt =
    raw?.dueAt && typeof raw.dueAt?.toDate === "function"
      ? raw.dueAt.toDate().toISOString()
      : raw?.dueAt || null;

  return {
    ...raw,
    dueAt,
    createdByUid: raw?.createdByUid || raw?.createdBy || null,
    status: raw?.status || "To Do",
    assignMode: raw?.assignMode || "users",
    distribution: raw?.distribution || "single",
    assignedToUserIds: Array.isArray(raw?.assignedToUserIds)
      ? raw.assignedToUserIds
      : [],
    memberIds: Array.isArray(raw?.memberIds) ? raw.memberIds : [],
    perUser: raw?.perUser || {},
    attachments: Array.isArray(raw?.attachments) ? raw.attachments : [],
    subtasks: Array.isArray(raw?.subtasks) ? raw.subtasks : [],
  };
}

function sanitizeAttachments(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter(Boolean)
    .map((a) => ({
      name: a?.name || "",
      type: a?.type || "file",
      url: a?.url || "",
      size: typeof a?.size === "number" ? a.size : null,
      contentType: a?.contentType || "",
    }))
    .filter((a) => a.name);
}

export const TaskService = {
  /**
   * Load tasks - rules handle visibility
   * ✅ FIX: end users query only tasks they can see (assignedToUserIds/memberIds/groupId)
   * ✅ FIX: avoids "permission-denied -> []" silent failure for end users
   */
  async getCompanyTasks(companyId, { me } = {}) {
    if (!companyId) throw new Error("Missing companyId");
    if (isCustomer(me)) return [];

    const tasksRef = collection(db, "companies", companyId, "tasks");

    const myRole = roleLower(me?.role);
    const isAdmin = myRole === "owner" || myRole === "admin";

    const myUid = me?.uid || auth.currentUser?.uid || null;
    const myIdCandidates = myUid ? [myUid] : [];

    const myGroupIds = Array.isArray(me?.groupIds) ? me.groupIds.filter(Boolean) : [];

    // Admin/Owner: load everything (fast path)
    if (isAdmin) {
      try {
        const snap = await getDocs(query(tasksRef, orderBy("updatedAt", "desc")));
        return snap.docs.map((d) => normalizeTask({ id: d.id, ...d.data() }));
      } catch (err) {
        console.error("[TaskService] getCompanyTasks(admin) error:", err);
        throw err;
      }
    }

    // EndUser: only load what they can see (rules-friendly)
    if (!myIdCandidates.length && !myGroupIds.length) return [];

    const seen = new Map(); // taskId -> task
    const addSnap = (snap) => {
      snap?.docs?.forEach((d) => {
        if (!seen.has(d.id)) seen.set(d.id, normalizeTask({ id: d.id, ...d.data() }));
      });
    };

    const chunk10 = (arr) => {
      const out = [];
      for (let i = 0; i < arr.length; i += 10) out.push(arr.slice(i, i + 10));
      return out;
    };

    try {
      // 1) assignedToUserIds contains my uid/docId
      for (const who of myIdCandidates) {
        try {
          const s = await getDocs(query(tasksRef, where("assignedToUserIds", "array-contains", who)));
          addSnap(s);
        } catch (e) {
          console.warn("[TaskService] assignedToUserIds query failed:", e?.code || e?.message);
        }
      }

      // 2) memberIds contains my uid (joint tasks)
      for (const who of myIdCandidates) {
        try {
          const s = await getDocs(query(tasksRef, where("memberIds", "array-contains", who)));
          addSnap(s);
        } catch (e) {
          console.warn("[TaskService] memberIds query failed:", e?.code || e?.message);
        }
      }

      // 3) groupId in myGroupIds (group tasks)
      for (const gChunk of chunk10(myGroupIds)) {
        if (!gChunk.length) continue;
        try {
          const s = await getDocs(query(tasksRef, where("groupId", "in", gChunk)));
          addSnap(s);
        } catch (e) {
          console.warn("[TaskService] groupId in query failed:", e?.code || e?.message);
        }
      }

      // 4) show tasks they created
      for (const who of myIdCandidates) {
        try {
          const s = await getDocs(query(tasksRef, where("createdByUid", "==", who)));
          addSnap(s);
        } catch (e) {
          console.warn("[TaskService] createdByUid query failed:", e?.code || e?.message);
        }
      }

      const toMillis = (v) => {
        if (!v) return 0;
        if (typeof v?.toMillis === "function") return v.toMillis();
        if (typeof v?.toDate === "function") return v.toDate().getTime();
        if (v instanceof Date) return v.getTime();
        if (typeof v === "number") return v;
        return 0;
      };

      return Array.from(seen.values()).sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
    } catch (err) {
      console.error("[TaskService] getCompanyTasks(enduser) error:", err);
      throw err;
    }
  },

  async getCompanyUsers(companyId) {
    if (!companyId) throw new Error("Missing companyId");
    const usersRef = collection(db, "users");
    const snap = await getDocs(query(usersRef, where("companyId", "==", companyId)));

    return snap.docs.map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        uid: data.uid || d.id,
        ...data,
      };
    });
  },

  async getGroupMemberIds(companyId, groupId) {
    if (!companyId || !groupId) throw new Error("Missing companyId or groupId");

    const users = await this.getCompanyUsers(companyId);

    return users
      .filter((u) => {
        const r = roleLower(u.role);
        if (!STAFF_ROLES.has(r)) return false;
        const gids = Array.isArray(u.groupIds) ? u.groupIds : [];
        return gids.includes(groupId);
      })
      .map((u) => u.uid || u.id)
      .filter(Boolean);
  },

  /**
   * Create task with support for:
   * - users mode: single or joint (if multiple users)
   * - group mode: joint OR soloCopies
   * ✅ Sends notifications after task creation
   * ✅ FIX: notifications are awaited + loud (Promise.allSettled)
   * ✅ NEW: Includes sample order linking fields (sampleOrderId, sampleOrderStatus, deliveredAt, productNames)
   */
  async createTask(companyId, payload) {
    if (!companyId) throw new Error("Missing companyId");

    const tasksRef = collection(db, "companies", companyId, "tasks");

    const createdByUid = payload.createdByUid || auth.currentUser?.uid || null;
    const createdByRole = roleLower(payload.createdByRole || "endUser") || "enduser";
    const creatorCanManageAssignments = isAdminOrOwnerRole(createdByRole);

    let dueAt = null;
    if (payload.dueDate) {
      const datePart = payload.dueDate;
      const timePart = payload.dueTime || "09:00";
      const dt = new Date(`${datePart}T${timePart}`);
      if (!Number.isNaN(dt.getTime())) {
        dueAt = Timestamp.fromDate(dt);
      }
    }

    const initialStatus = payload.status || "To Do";

    const base = {
      title: payload.title || "",
      description: payload.description || "",
      status: initialStatus,
      priority: payload.priority || "Medium",
      type: payload.type || "General",
      recurrence: payload.recurrence || "None",
      customRecurrence: payload.customRecurrence || "",
      accountId: payload.accountId || "",
      estimatedHours: Number(payload.estimatedHours || 1),
      attachments: sanitizeAttachments(payload.attachments),
      dueAt,
      createdByUid,
      createdByRole,
      updatedByUid: payload.updatedByUid || createdByUid,
      updatedByRole: roleLower(payload.updatedByRole || createdByRole) || createdByRole,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // 🔥 Sample order linking fields
      sampleOrderId: payload.sampleOrderId || null,
      sampleOrderStatus: payload.sampleOrderStatus || null,
      convertedToOrderOnComplete: !!payload.convertedToOrderOnComplete,
      deliveredAt: payload.deliveredAt || null,
      productNames: Array.isArray(payload.productNames) ? payload.productNames : [],
      subtasks: Array.isArray(payload.subtasks) ? payload.subtasks : [],
    };

    const sanitizedPayload = creatorCanManageAssignments
      ? payload
      : {
          ...payload,
          assignMode: "users",
          assignedToUserIds: createdByUid ? [createdByUid] : [],
          groupId: "",
          groupName: "",
          soloCopies: false,
        };

    const assignMode = sanitizedPayload.assignMode || "users";
    const taskTitle = sanitizedPayload.title || "New task";

    let assignedByName = "Someone";
    if (createdByUid) {
      try {
        const creatorDoc = await getDoc(doc(db, "users", createdByUid));
        if (creatorDoc.exists()) {
          const creatorData = creatorDoc.data();
          assignedByName =
            creatorData.displayName ||
            creatorData.fullName ||
            creatorData.name ||
            creatorData.email ||
            "Someone";
        }
      } catch (e) {
        console.warn("[TaskService] Could not load creator name for notification:", e);
      }
    }

    // -------- USERS MODE --------
    if (assignMode === "users") {
      const assignedToUserIds = Array.isArray(sanitizedPayload.assignedToUserIds)
        ? sanitizedPayload.assignedToUserIds.filter(Boolean)
        : [];

      const distribution = assignedToUserIds.length > 1 ? "joint" : "single";

      if (distribution === "joint") {
        const memberIds = [...new Set(assignedToUserIds)];
        const perUser = {};
        memberIds.forEach((uid) => {
          perUser[uid] = { status: initialStatus, updatedAt: null, completedAt: null };
        });

        const jointDoc = {
          ...base,
          assignMode: "users",
          distribution: "joint",
          assignedToUserIds: memberIds,
          memberIds,
          perUser,
        };

        const ref = await addDoc(tasksRef, jointDoc);

        const results = await Promise.allSettled(
          memberIds.map((userId) =>
            notifyTaskAssignedToUser({
              companyId,
              taskId: ref.id,
              taskTitle,
              assignedUserId: userId,
              assignedByName,
            })
          )
        );

        results.forEach((r, i) => {
          if (r.status === "rejected") {
            console.error("[TaskService] notifyTaskAssignedToUser FAILED", {
              assignedUserId: memberIds[i],
              code: r.reason?.code,
              message: r.reason?.message,
              reason: r.reason,
            });
          }
        });

        return { createdTaskIds: [ref.id] };
      }

      const singleDoc = {
        ...base,
        assignMode: "users",
        distribution: "single",
        assignedToUserIds,
      };

      const ref = await addDoc(tasksRef, singleDoc);

      if (assignedToUserIds.length === 1) {
        const r = await Promise.allSettled([
          notifyTaskAssignedToUser({
            companyId,
            taskId: ref.id,
            taskTitle,
            assignedUserId: assignedToUserIds[0],
            assignedByName,
          }),
        ]);

        if (r[0].status === "rejected") {
          console.error("[TaskService] notifyTaskAssignedToUser FAILED", {
            assignedUserId: assignedToUserIds[0],
            code: r[0].reason?.code,
            message: r[0].reason?.message,
            reason: r[0].reason,
          });
        }
      }

      return { createdTaskIds: [ref.id] };
    }

    // -------- GROUP MODE --------
    if (assignMode === "group") {
      const groupId = sanitizedPayload.groupId || "";
      const groupName = sanitizedPayload.groupName || "";
      if (!groupId) throw new Error("Missing groupId for group task");

      const memberIds = await this.getGroupMemberIds(companyId, groupId);

      if (!memberIds.length) {
        throw new Error("This group has no staff members to assign tasks to.");
      }


      const createdTaskIds = [];

      for (const uid of memberIds) {
        const copyDoc = {
          ...base,
          assignMode: "group",
          distribution: "soloCopies",
          groupId,
          groupName,
          assignedToUserIds: [uid],
        };

        const ref = await addDoc(tasksRef, copyDoc);
        createdTaskIds.push(ref.id);

        const r = await Promise.allSettled([
          notifyTaskAssignedToUser({
            companyId,
            taskId: ref.id,
            taskTitle,
            assignedUserId: uid,
            assignedByName,
          }),
        ]);

        if (r[0].status === "rejected") {
          console.error("[TaskService] notifyTaskAssignedToUser FAILED (soloCopies)", {
            assignedUserId: uid,
            code: r[0].reason?.code,
            message: r[0].reason?.message,
            reason: r[0].reason,
          });
        }
      }

      const groupNotify = await Promise.allSettled([
        notifyTaskAssignedToGroup({
          companyId,
          taskId: createdTaskIds[0] || "group-copies",
          taskTitle,
          groupId,
          groupName,
          recipientUserIds: memberIds,
        }),
      ]);

      if (groupNotify[0].status === "rejected") {
        console.error("[TaskService] notifyTaskAssignedToGroup FAILED", {
          groupId,
          code: groupNotify[0].reason?.code,
          message: groupNotify[0].reason?.message,
          reason: groupNotify[0].reason,
        });
      }

      return { createdTaskIds };
    }

    throw new Error("Invalid assignMode");
  },

  /**
   * Update sample order when follow-up task is completed
   * Only runs if sampleOrder is currently "Follow Up"
   */
  async updateSampleOrderFromTask(companyId, taskId, taskData) {
    if (!companyId || !taskId || !taskData) {
      console.error("Missing required parameters for updateSampleOrderFromTask");
      return;
    }

    try {
      if (!taskData.sampleOrderId) return;

      const sampleOrderRef = doc(db, "companies", companyId, "sampleOrders", taskData.sampleOrderId);

      const sampleOrderSnap = await getDoc(sampleOrderRef);
      if (!sampleOrderSnap.exists()) return;

      const sampleOrderData = sampleOrderSnap.data();

      const currentStatus = String(sampleOrderData.status || "").trim();
      if (["Completed", "Converted to Order", "Cancelled"].includes(currentStatus)) return;

      const updates = {
        status: taskData.convertedToOrderOnComplete ? "Converted to Order" : "Completed",
        convertedToOrder: !!taskData.convertedToOrderOnComplete,
        isConverted: !!taskData.convertedToOrderOnComplete,
        statusUpdatedAt: serverTimestamp(),
        statusUpdatedBy: taskData.updatedByUid || "system",
        completedAt: taskData.convertedToOrderOnComplete ? null : serverTimestamp(),
        completedBy: taskData.convertedToOrderOnComplete ? null : taskData.updatedByUid || null,
        convertedToOrderAt: taskData.convertedToOrderOnComplete ? serverTimestamp() : null,
        convertedToOrderBy: taskData.convertedToOrderOnComplete ? taskData.updatedByUid || null : null,
        followUpCompletedAt: serverTimestamp(),
        followUpCompletedBy: taskData.updatedByUid || null,
        followUpTaskCompletedAt: serverTimestamp(),
        followUpOutcome: taskData.convertedToOrderOnComplete ? "converted_to_order" : "closed",
        followUpNotes: taskData.description || taskData.notes || "",
        updatedAt: serverTimestamp(),
      };

      await updateDoc(sampleOrderRef, updates);
      return updates;
    } catch (error) {
      console.error("❌ Failed to update sample order from task completion:", error);
      throw error;
    }
  },

  /**
   * ✅ Update task (NO “CONFIRMED” completion)
   * - any actor who sets status="Completed" stamps completedAt/completedByUid
   * - reverse-flow sampleOrder update fires on any completion transition
   * - notifications fire on assignment changes
   */
  async updateTask(companyId, taskId, updates) {
    if (!companyId || !taskId) throw new Error("Missing companyId or taskId");

    const taskRef = doc(db, "companies", companyId, "tasks", taskId);

    let oldTask = null;
    let shouldNotify = false;

    try {
      const oldTaskSnap = await getDoc(taskRef);
      if (oldTaskSnap.exists()) {
        oldTask = oldTaskSnap.data();

        const oldUserIds = new Set(oldTask.assignedToUserIds || []);
        const newUserIds = new Set(updates.assignedToUserIds || []);
        const oldGroupId = oldTask.groupId;
        const newGroupId = updates.groupId;

        shouldNotify =
          (updates.assignedToUserIds &&
            JSON.stringify([...oldUserIds].sort()) !== JSON.stringify([...newUserIds].sort())) ||
          (updates.groupId && oldGroupId !== newGroupId);
      }
    } catch (e) {
      console.warn("[TaskService] Could not load old task for notification check:", e);
    }

    // Handle dueDate/dueTime -> dueAt
    let dueAt = undefined;
    if ("dueDate" in updates) {
      if (updates.dueDate) {
        const timePart = updates.dueTime || "09:00";
        const dt = new Date(`${updates.dueDate}T${timePart}`);
        dueAt = Number.isNaN(dt.getTime()) ? null : Timestamp.fromDate(dt);
      } else {
        dueAt = null;
      }
    }

    const actorUid = auth.currentUser?.uid || updates.updatedByUid || null;
    const actorRole = roleLower(updates.updatedByRole || "") || undefined;
    const actorCanManageAssignments = isAdminOrOwnerRole(actorRole || oldTask?.updatedByRole || oldTask?.createdByRole || "");

    const patch = {
      ...updates,
      ...(dueAt !== undefined ? { dueAt } : {}),
      ...(Object.prototype.hasOwnProperty.call(updates, "attachments")
        ? { attachments: sanitizeAttachments(updates.attachments) }
        : {}),
      ...(actorUid ? { updatedByUid: actorUid } : {}),
      ...(actorRole ? { updatedByRole: actorRole } : {}),
      updatedAt: serverTimestamp(),
    };

    // Strip UI-only / protected fields
    delete patch.dueDate;
    delete patch.dueTime;
    delete patch.soloCopies;

    delete patch.createdByUid;
    delete patch.createdByRole;
    delete patch.createdAt;

    if (!actorCanManageAssignments) {
      delete patch.assignMode;
      delete patch.groupId;
      delete patch.groupName;
      delete patch.distribution;
      delete patch.memberIds;
      delete patch.perUser;
      patch.assignedToUserIds = actorUid ? [actorUid] : oldTask?.assignedToUserIds || [];
    }

    // ✅ SIMPLE completion stamping (NO confirmed logic)
    const isBeingCompleted = patch.status === "Completed" && oldTask?.status !== "Completed";

    if (patch.status === "Completed") {
      patch.completedByUid = actorUid || null;
      patch.completedByRole = actorRole || null;
      patch.completedAt = serverTimestamp();
    }

    // If moving away from Completed explicitly, clear completion fields
    if (Object.prototype.hasOwnProperty.call(patch, "status") && patch.status !== "Completed") {
      patch.completedByUid = null;
      patch.completedByRole = null;
      patch.completedAt = null;
    }

    await updateDoc(taskRef, patch);

    // 🔥 Reverse-flow sample order update on any completion transition
    if (isBeingCompleted && oldTask?.sampleOrderId) {
      try {
        const convertedOnComplete =
          Object.prototype.hasOwnProperty.call(patch, "convertedToOrderOnComplete")
            ? !!patch.convertedToOrderOnComplete
            : !!oldTask?.convertedToOrderOnComplete;

        await this.updateSampleOrderFromTask(companyId, taskId, {
          ...oldTask,
          ...patch,
          sampleOrderId: oldTask.sampleOrderId,
          convertedToOrderOnComplete: convertedOnComplete,
          updatedByUid: actorUid || oldTask.updatedByUid,
        });
      } catch (sampleOrderError) {
        console.error("Failed to update sample order after completion:", sampleOrderError);
      }
    }

    // Notifications on assignment changes
    if (shouldNotify && oldTask) {
      const taskTitle = updates.title || oldTask.title || "Task";

      let assignedByName = "Someone";
      if (actorUid) {
        try {
          const updaterDoc = await getDoc(doc(db, "users", actorUid));
          if (updaterDoc.exists()) {
            const updaterData = updaterDoc.data();
            assignedByName =
              updaterData.displayName ||
              updaterData.fullName ||
              updaterData.name ||
              updaterData.email ||
              "Someone";
          }
        } catch (e) {
          console.warn("[TaskService] Could not load updater name for notification:", e);
        }
      }

      if (updates.assignedToUserIds) {
        const oldUserIds = new Set(oldTask.assignedToUserIds || []);
        const newUserIds = Array.isArray(updates.assignedToUserIds)
          ? updates.assignedToUserIds.filter(Boolean)
          : [];

        const newlyAssignedUsers = newUserIds.filter((uid) => !oldUserIds.has(uid));

        if (newlyAssignedUsers.length) {
          const results = await Promise.allSettled(
            newlyAssignedUsers.map((userId) =>
              notifyTaskAssignedToUser({
                companyId,
                taskId,
                taskTitle,
                assignedUserId: userId,
                assignedByName,
              })
            )
          );

          results.forEach((r, i) => {
            if (r.status === "rejected") {
              console.error("[TaskService] notifyTaskAssignedToUser FAILED (update)", {
                assignedUserId: newlyAssignedUsers[i],
                code: r.reason?.code,
                message: r.reason?.message,
                reason: r.reason,
              });
            }
          });
        }
      }

      if (updates.groupId && oldTask.groupId !== updates.groupId) {
        const groupName = updates.groupName || "your team";
        const memberIds = await this.getGroupMemberIds(companyId, updates.groupId);

        const r = await Promise.allSettled([
          notifyTaskAssignedToGroup({
            companyId,
            taskId,
            taskTitle,
            groupId: updates.groupId,
            groupName,
            recipientUserIds: memberIds,
          }),
        ]);

        if (r[0].status === "rejected") {
          console.error("[TaskService] notifyTaskAssignedToGroup FAILED (update)", {
            groupId: updates.groupId,
            code: r[0].reason?.code,
            message: r[0].reason?.message,
            reason: r[0].reason,
          });
        }
      }
    }
  },

  /**
   * For joint tasks: update ONLY the current user's status under perUser[uid]
   * ✅ writes updatedByUid/updatedByRole
   * ✅ Updates linked sample order if all members become Completed
   */
  async updateMyJointStatus(companyId, taskId, uid, status, role) {
    if (!companyId || !taskId || !uid) throw new Error("Missing params");

    const taskRef = doc(db, "companies", companyId, "tasks", taskId);

    const normalizedStatus = status || "To Do";
    const isDone = normalizedStatus === "Completed";

    await updateDoc(taskRef, {
      [`perUser.${uid}.status`]: normalizedStatus,
      [`perUser.${uid}.updatedAt`]: serverTimestamp(),
      ...(isDone ? { [`perUser.${uid}.completedAt`]: serverTimestamp() } : {}),
      updatedByUid: uid,
      ...(role ? { updatedByRole: roleLower(role) } : {}),
      updatedAt: serverTimestamp(),
    });

    if (isDone) {
      try {
        const taskSnap = await getDoc(taskRef);
        if (taskSnap.exists()) {
          const taskData = taskSnap.data();

          if (taskData.sampleOrderId && taskData.distribution === "joint") {
            const perUser = taskData.perUser || {};
            const memberIds = taskData.memberIds || [];

            const allCompleted = memberIds.every((memberId) => perUser[memberId]?.status === "Completed");

            if (allCompleted) {
              await this.updateSampleOrderFromTask(companyId, taskId, taskData);
            }
          }
        }
      } catch (error) {
        console.error("Failed to check joint task completion status:", error);
      }
    }
  },

  async deleteTask(companyId, taskId) {
    if (!companyId || !taskId) throw new Error("Missing companyId or taskId");
    const taskRef = doc(db, "companies", companyId, "tasks", taskId);
    const taskSnap = await getDoc(taskRef);
    if (!taskSnap.exists()) throw new Error("Task not found.");
    const taskData = taskSnap.data() || {};

    await moveDocToRecycleBin({
      db,
      companyId,
      entityType: "task",
      entityId: taskId,
      data: taskData,
      displayName: taskData.title || taskId,
      deletedByUid: auth.currentUser?.uid || null,
      deletedByRole: taskData.updatedByRole || null,
      metadata: {
        sampleOrderId: taskData.sampleOrderId || null,
      },
    });

    await deleteDoc(taskRef);
  },

  async getTasksBySampleOrderId(companyId, sampleOrderId) {
    if (!companyId || !sampleOrderId) return [];

    try {
      const tasksRef = collection(db, "companies", companyId, "tasks");
      const q = query(tasksRef, where("sampleOrderId", "==", sampleOrderId));
      const snap = await getDocs(q);

      return snap.docs.map((d) => normalizeTask({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Error getting tasks by sample order ID:", error);
      return [];
    }
  },
};
